import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { DURATION, EASE, STAGGER } from '../../shared/motion.js'

gsap.registerPlugin(SplitText)

// ─────────────────────────────────────────────────────────────
// Private Low-Level Motion Helpers (Stateless)
// ─────────────────────────────────────────────────────────────

/** 正規化 DOM 輸入，確保 SplitText 接收到有效的 Element 陣列 */
function _toElementArray(els) {
    if (!els) {
        return []
    }
    if (Array.isArray(els)) {
        return els.filter(Boolean)
    }
    if (els instanceof NodeList || els instanceof HTMLCollection) {
        return Array.from(els)
    }
    return [els]
}

/**
 * 對「已經 split 好」的 lines 直接下 tween，不重新 split
 * 中斷進行中的動畫改變方向時可以呼叫這個，讓新動畫從目前實際位置繼續，不會歸零重跳
 */
function tweenLines(lines, dir, { y = '100%', stagger = STAGGER.tight, delay = 0 } = {}) {
    const isIn = dir === 'in'

    return gsap[isIn ? 'from' : 'to'](lines, {
        y: isIn ? y : `-${y}`,
        stagger,
        delay,
        duration: isIn ? DURATION.reveal : DURATION.conceal,
        ease: isIn ? EASE.reveal : EASE.conceal
    })
}

/**
 * 逐行揭露/隱藏的核心動畫邏輯
 * 將目標元素切割為 lines 並加上遮罩，實現由下至上的滑入 (in) 與滑出 (out)
 *
 * @returns {{ split: SplitText, tween: gsap.core.Tween }}
 */
function slideLines(els, dir, { y = '100%', stagger = STAGGER.tight, delay = 0, linesClass = '' } = {}) {
    const validEls = _toElementArray(els)
    if (validEls.length === 0) {
        return { split: null, tween: null }
    }

    const split = SplitText.create(validEls, { type: 'lines', mask: 'lines', linesClass })
    const tween = tweenLines(split.lines, dir, { y, stagger, delay })

    return { split, tween }
}

/** 從遮罩外滑入 (顯示) */
export function splitReveal(els, opts) {
    return slideLines(els, 'in', opts)
}

/** 往遮罩外滑出 (隱藏) */
export function splitConceal(els, opts) {
    return slideLines(els, 'out', opts)
}

/**
 * 靜默重建 lines 分割結構，不觸發任何 tween
 * 用於 RWD resize 時修正斷行與遮罩，讓 split 結構跟上目前實際排版（不影響當下顯示狀態）
 */
export function resplitLines(els) {
    const validEls = _toElementArray(els)
    if (validEls.length === 0) {
        return null
    }

    return SplitText.create(validEls, { type: 'lines', mask: 'lines' })
}

// ─────────────────────────────────────────────────────────────
// Stateful Split Manager
// ─────────────────────────────────────────────────────────────

/**
 * SplitFx：包辦單一文字元素的 SplitText 全生命週期
 * 自動維護 split 建立、revert 與內容清空，避免多次重複 split 產生的 DOM 污染
 */
export class SplitFx {
    constructor(el) {
        this.el = el
        this.split = null
        this.tween = null
    }

    _cleanup() {
        this.tween?.kill()
        this.split?.revert()
        this.tween = null
        this.split = null
    }

    /**
     * 設定文字並從遮罩外滑入
     * @param {string|null} [text] 若傳入則更換文字，省略則沿用現有文字
     */
    reveal(text, opts = {}) {
        this.revert()

        if (text != null) {
            if (opts.html) {
                this.el.innerHTML = text
            } else {
                this.el.textContent = text
            }
        }
        if (!this.el?.textContent) {
            return null
        }

        const { split, tween } = splitReveal([this.el], opts)
        this.split = split
        this.tween = tween

        return tween
    }

    /**
     * 往遮罩外滑出，動畫完成後自動清空文字與 Split 結構
     * 若目前這個元素還在 reveal 動畫進行中（split 尚未被清掉），直接在同一組已存在的
     * lines 上反向下 tween，從目前實際位置接續往外滑，不重新 split／不跳位
     * （中斷不等待、不歸零重來——快速連續觸發也不會有瞬間跳到全顯示再滑出的閃爍）
     */
    conceal(opts = {}) {
        if (!this.el?.textContent) {
            return null
        }

        let split
        let tween
        if (this.split?.lines?.length) {
            this.tween?.kill()
            split = this.split
            tween = tweenLines(split.lines, 'out', opts)
        } else {
            this._cleanup()
            ;({ split, tween } = splitConceal([this.el], opts))
        }

        this.split = split
        this.tween = tween

        tween?.eventCallback('onComplete', () => {
            // 防止該動畫進行中被新的 reveal 覆蓋，僅在還是同一組 split 時才執行清空
            if (this.split !== split) {
                return
            }
            this.clear()
        })

        return tween
    }

    /** 立即停止動畫並還原 DOM（保留文字內容） */
    revert() {
        this._cleanup()
    }

    /** 立即停止動畫、還原 DOM 並清空文字 */
    clear() {
        this._cleanup()
        if (this.el) {
            this.el.textContent = ''
        }
    }
}
